import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  CalendarDayConfiguration,
  CreateCalendarDayConfigPayload,
  CreateCalendarRangeConfigPayload,
  GetCalendarDayConfigParams,
  GetCalendarRangeConfigParams,
} from '../types/calendar-config.types';
import type {
  BookingsByCustomerPhoneParams,
  BookingHistoryDetails,
  BookingHistoryDetailsParams,
  BookingHistoryItem,
  BookingHistoryParams,
  BookingStatus,
  BookingFileUploadResponse,
  CreateBookingPayload,
  CreateBookingResponse,
  CustomerDeleteBookingParams,
  CustomerUpdateBookingPayload,
  DownloadBookingHistoryPdfParams,
  DownloadBookingHistoryPdfResult,
  UpdateBookingMutationArg,
  UpdateBookingStatusRequest,
  RemoveBookingFileParams,
  UploadBookingFileParams,
  UploadMultimediaParams,
  HistoricalCalendarDay,
  HistoricalCalendarParams,
  Pro20Page,
} from '../types/booking.types';
import { normalizeBookingFileUploadResponse } from '../utils/normalize-booking-file-upload';
import { normalizeBookingHistoryResponse } from '../utils/normalize-booking-history';

function resolveStatuses({
  statusKey,
  statuses,
}: Pick<BookingHistoryParams, 'statusKey' | 'statuses'>): BookingStatus[] {
  if (statuses) {
    return [...statuses];
  }
  if (statusKey) {
    return statusKey.split(',').filter(Boolean) as BookingStatus[];
  }
  return [];
}

function buildBookingHistoryUrl({
  branchCode,
  page = 0,
  size = 20,
  statusKey,
  statuses,
  search,
  sortBy,
  sortDirection,
  bookingDate,
  timeRangeCode,
  timeRangeFilter,
}: BookingHistoryParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  resolveStatuses({ statusKey, statuses })
    .sort()
    .forEach((status) => {
      params.append('status[]', status);
    });

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  if (sortBy) {
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection ?? 'asc');
  }

  if (bookingDate) {
    params.set('bookingDate', bookingDate);
  }

  if (timeRangeCode) {
    params.set('timeRangeCode', timeRangeCode);
  }

  if (timeRangeFilter) {
    params.set('timeRangeFilter', timeRangeFilter);
  }

  return `/branches/${encodeURIComponent(branchCode)}/bookings/history/list?${params.toString()}`;
}

function buildHistoricalCalendarUrl({
  branchCode,
  fromDate,
  toDate,
}: HistoricalCalendarParams): string {
  const params = new URLSearchParams();
  params.set('fromDate', fromDate);
  params.set('toDate', toDate);
  return `/branches/${branchCode}/bookings/historical-calendar?${params.toString()}`;
}

function buildDownloadBookingHistoryPdfUrl({
  branchCode,
  lang,
  search,
  statuses,
  sortBy,
  sortDirection,
  bookingDate,
  timeRangeCode,
  timeRangeFilter,
}: DownloadBookingHistoryPdfParams): string {
  const params = new URLSearchParams();
  params.set('search', search?.trim() ?? '');
  params.set('lang', lang);

  (statuses ?? []).forEach((status) => {
    params.append('status[]', status);
  });

  if (sortBy) {
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection ?? 'asc');
  }

  if (bookingDate) {
    params.set('bookingDate', bookingDate);
  }

  if (timeRangeCode) {
    params.set('timeRangeCode', timeRangeCode);
  }

  if (timeRangeFilter) {
    params.set('timeRangeFilter', timeRangeFilter);
  }

  return `/branches/${encodeURIComponent(branchCode)}/bookings/history/pdf?${params.toString()}`;
}

function getBookingFileInvalidationTags({
  bookingCode,
  branchCode,
}: {
  bookingCode: string;
  branchCode?: string;
}) {
  const tags: Array<{ type: typeof API_TAGS.Bookings; id: string }> = [
    { type: API_TAGS.Bookings, id: bookingCode },
  ];

  if (branchCode) {
    tags.push({ type: API_TAGS.Bookings, id: branchCode });
  }

  return tags;
}

export const bookingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBookingHistory: builder.query<
      PaginatedResponse<BookingHistoryItem>,
      BookingHistoryParams
    >({
      query: (args) => ({
        url: buildBookingHistoryUrl(args),
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          branchCode,
          page = 0,
          size = 20,
          statusKey = '',
          search = '',
          sortBy = '',
          sortDirection = 'asc',
          bookingDate = '',
          timeRangeCode = '',
          timeRangeFilter = '',
        } = queryArgs;
        return `${endpointName}(${branchCode}|${String(page)}|${String(size)}|${statusKey}|${search}|${sortBy}|${sortDirection}|${bookingDate}|${timeRangeCode}|${timeRangeFilter})`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg || !currentArg) {
          return true;
        }
        return (
          currentArg.statusKey !== previousArg.statusKey ||
          currentArg.search !== previousArg.search ||
          currentArg.bookingDate !== previousArg.bookingDate ||
          currentArg.page !== previousArg.page ||
          currentArg.size !== previousArg.size ||
          currentArg.sortBy !== previousArg.sortBy ||
          currentArg.sortDirection !== previousArg.sortDirection ||
          currentArg.branchCode !== previousArg.branchCode ||
          currentArg.timeRangeCode !== previousArg.timeRangeCode ||
          currentArg.timeRangeFilter !== previousArg.timeRangeFilter
        );
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<
              Pro20Page<BookingHistoryItem> | BookingHistoryItem[]
            >
          | Pro20Page<BookingHistoryItem>
          | BookingHistoryItem[],
        _meta,
        arg,
      ) => {
        const results = normalizePro20Response(response);
        return normalizeBookingHistoryResponse(results, arg.size ?? 20);
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),
    getHistoricalCalendar: builder.query<
      HistoricalCalendarDay[],
      HistoricalCalendarParams
    >({
      query: (args) => ({
        url: buildHistoricalCalendarUrl(args),
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { branchCode, fromDate, toDate } = queryArgs;
        return `${endpointName}(${branchCode}|${fromDate}|${toDate})`;
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<HistoricalCalendarDay[]>
          | HistoricalCalendarDay[],
      ) => {
        const results = normalizePro20Response(response);
        return Array.isArray(results) ? results : [];
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
      ],
    }),
    getBookingHistoryDetails: builder.query<
      BookingHistoryDetails,
      BookingHistoryDetailsParams
    >({
      query: ({ bookingCode }) => ({
        url: `/bookings/${bookingCode}`,
        method: 'GET',
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<BookingHistoryDetails>
          | BookingHistoryDetails,
      ) => {
        return normalizePro20Response(response);
      },
      providesTags: (_result, _error, { bookingCode }) => [
        {
          type: API_TAGS.Bookings,
          id: bookingCode,
        },
      ],
    }),
    createBooking: builder.mutation<
      CreateBookingResponse,
      CreateBookingPayload
    >({
      query: (body) => ({
        url: `/booking/create`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CreateBookingResponse>
          | CreateBookingResponse,
      ) => {
        return normalizePro20Response(response);
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Bookings, id: branchCode },
        { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
      ],
    }),
    updateBooking: builder.mutation<
      CreateBookingResponse,
      UpdateBookingMutationArg
    >({
      query: ({ payload, notifyCustomer = false }) => ({
        url: `/booking/update/bookingdatastructure?notifyCustomer=${String(notifyCustomer)}`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CreateBookingResponse>
          | CreateBookingResponse,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { payload }) => [
        { type: API_TAGS.Bookings, id: payload.branchCode },
        { type: API_TAGS.Bookings, id: `${payload.branchCode}-calendar` },
        { type: API_TAGS.Bookings, id: payload.bookingCode },
      ],
    }),
    customerUpdateBooking: builder.mutation<
      CreateBookingResponse,
      CustomerUpdateBookingPayload
    >({
      query: (body) => ({
        url: `/booking/customer/update`,
        method: 'PUT',
        body,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CreateBookingResponse>
          | CreateBookingResponse,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, payload) => [
        { type: API_TAGS.Bookings, id: payload.branchCode },
        { type: API_TAGS.Bookings, id: `${payload.branchCode}-calendar` },
        { type: API_TAGS.Bookings, id: payload.bookingCode },
        { type: API_TAGS.Dashboard, id: payload.branchCode },
        API_TAGS.Dashboard,
      ],
    }),
    customerDeleteBooking: builder.mutation<
      BookingHistoryDetails,
      CustomerDeleteBookingParams
    >({
      query: ({ bookingCode }) => ({
        url: `/booking/customer/delete/${encodeURIComponent(bookingCode)}`,
        method: 'DELETE',
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<BookingHistoryDetails>
          | BookingHistoryDetails,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { bookingCode, branchCode }) => [
        ...(branchCode
          ? [
              { type: API_TAGS.Bookings, id: branchCode } as const,
              {
                type: API_TAGS.Bookings,
                id: `${branchCode}-calendar`,
              } as const,
              { type: API_TAGS.Dashboard, id: branchCode } as const,
            ]
          : []),
        { type: API_TAGS.Bookings, id: bookingCode },
        API_TAGS.Dashboard,
      ],
    }),
    updateBookingStatus: builder.mutation<unknown, UpdateBookingStatusRequest>({
      query: ({
        bookingCode,
        status,
        processedBy,
        alertCustomer = false,
        tableCode,
      }) => {
        const params = new URLSearchParams({
          status,
          processedBy,
          alertCustomer: String(alertCustomer),
        });
        const resolvedTableCode = tableCode?.trim();
        if (resolvedTableCode) {
          params.set('tableCode', resolvedTableCode);
        }

        return {
          url: `booking/update/bookingstatus/${encodeURIComponent(bookingCode)}?${params.toString()}`,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, { branchCode, bookingCode }) => [
        { type: API_TAGS.Bookings, id: branchCode },
        { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
        { type: API_TAGS.Bookings, id: bookingCode },
        { type: API_TAGS.Dashboard, id: branchCode },
        API_TAGS.Dashboard,
      ],
    }),
    uploadBookingImage: builder.mutation<
      BookingFileUploadResponse,
      UploadBookingFileParams
    >({
      query: ({ bookingCode, formData }) => ({
        url: `/booking/upload/${bookingCode}/image`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBookingFileUploadResponse(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { bookingCode, branchCode }) =>
        getBookingFileInvalidationTags({ bookingCode, branchCode }),
    }),
    uploadBookingPdf: builder.mutation<
      BookingFileUploadResponse,
      UploadBookingFileParams
    >({
      query: ({ bookingCode, formData }) => ({
        url: `/booking/upload/${bookingCode}/pdf`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBookingFileUploadResponse(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { bookingCode, branchCode }) =>
        getBookingFileInvalidationTags({ bookingCode, branchCode }),
    }),
    removeBookingImage: builder.mutation<null, RemoveBookingFileParams>({
      query: ({ bookingCode }) => ({
        url: `/booking/remove/${bookingCode}/image`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { bookingCode, branchCode }) =>
        getBookingFileInvalidationTags({ bookingCode, branchCode }),
    }),
    removeBookingPdf: builder.mutation<null, RemoveBookingFileParams>({
      query: ({ bookingCode }) => ({
        url: `/booking/remove/${bookingCode}/pdf`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { bookingCode, branchCode }) =>
        getBookingFileInvalidationTags({ bookingCode, branchCode }),
    }),
    uploadMultimedia: builder.mutation<
      BookingFileUploadResponse,
      UploadMultimediaParams
    >({
      query: ({ formData }) => ({
        url: '/multimedia/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBookingFileUploadResponse(normalizePro20Response(response)),
    }),
    downloadBookingHistoryPdf: builder.query<
      DownloadBookingHistoryPdfResult,
      DownloadBookingHistoryPdfParams
    >({
      query: (args) => ({
        url: buildDownloadBookingHistoryPdfUrl(args),
        method: 'GET',
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<DownloadBookingHistoryPdfResult>
          | DownloadBookingHistoryPdfResult,
      ) => {
        const results = normalizePro20Response(response) as
          | DownloadBookingHistoryPdfResult
          | null
          | undefined;

        const pdfUrl =
          results && typeof results === 'object' && 'pdfUrl' in results
            ? results.pdfUrl.trim()
            : '';

        if (!pdfUrl) {
          throw new Error('Missing booking history PDF URL');
        }

        return { pdfUrl };
      },
    }),
    getBookingsByCustomerPhone: builder.query<
      BookingHistoryItem[],
      BookingsByCustomerPhoneParams
    >({
      query: ({ prefix, phone, branchCode }) => ({
        url: `/booking/retrievebyphone/${encodeURIComponent(prefix)}/${encodeURIComponent(phone)}/${encodeURIComponent(branchCode)}`,
        method: 'GET',
      }),
      transformResponse: (
        response: Pro20ApiResponse<BookingHistoryItem[]> | BookingHistoryItem[],
      ) => {
        const results = normalizePro20Response(response);
        return Array.isArray(results) ? results : [];
      },
    }),
    getCalendarDayConfig: builder.query<
      CalendarDayConfiguration,
      GetCalendarDayConfigParams
    >({
      query: ({ branchCode, bookingDate }) => ({
        url: `/calendar-config/get/${encodeURIComponent(branchCode)}/${encodeURIComponent(bookingDate)}`,
        method: 'GET',
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CalendarDayConfiguration>
          | CalendarDayConfiguration,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode, bookingDate }) => [
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-${bookingDate}`,
        },
      ],
    }),
    getCalendarRangeConfig: builder.query<
      Pro20Page<CalendarDayConfiguration>,
      GetCalendarRangeConfigParams
    >({
      query: ({
        branchCode,
        bookingDate,
        bookingDateTo,
        page = 0,
        size = 25,
        sortBy = 'date',
        sortDirection = 'asc',
      }) => ({
        url: `/calendar-config/get/${encodeURIComponent(branchCode)}/${encodeURIComponent(bookingDate)}/${encodeURIComponent(bookingDateTo)}`,
        method: 'GET',
        params: {
          page,
          size,
          sortBy,
          sortDirection,
        },
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<Pro20Page<CalendarDayConfiguration>>
          | Pro20Page<CalendarDayConfiguration>,
      ) => normalizePro20Response(response),
      providesTags: (
        _result,
        _error,
        { branchCode, bookingDate, bookingDateTo },
      ) => [
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-range-${bookingDate}-${bookingDateTo}`,
        },
      ],
    }),
    createCalendarDayConfig: builder.mutation<
      CalendarDayConfiguration,
      CreateCalendarDayConfigPayload
    >({
      query: (body) => ({
        url: '/calendar-config/create',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CalendarDayConfiguration>
          | CalendarDayConfiguration,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode, date }) => [
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-${date}`,
        },
        { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),
    createOrUpdateCalendarRangeConfig: builder.mutation<
      CalendarDayConfiguration,
      CreateCalendarRangeConfigPayload
    >({
      query: (body) => ({
        url: '/calendar-config/range/create-or-update',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<CalendarDayConfiguration>
          | CalendarDayConfiguration,
      ) => normalizePro20Response(response),
      invalidatesTags: (
        _result,
        _error,
        { branchCode, startDate, endDate },
      ) => [
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-${startDate}`,
        },
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-${endDate}`,
        },
        {
          type: API_TAGS.Bookings,
          id: `${branchCode}-calendar-config-range-${startDate}-${endDate}`,
        },
        { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
        { type: API_TAGS.Bookings, id: branchCode },
      ],
    }),
  }),
});

export const {
  useGetBookingHistoryQuery,
  useGetHistoricalCalendarQuery,
  useGetBookingHistoryDetailsQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useCustomerUpdateBookingMutation,
  useCustomerDeleteBookingMutation,
  useUpdateBookingStatusMutation,
  useUploadBookingImageMutation,
  useUploadBookingPdfMutation,
  useRemoveBookingImageMutation,
  useRemoveBookingPdfMutation,
  useUploadMultimediaMutation,
  useLazyGetBookingsByCustomerPhoneQuery,
  useLazyDownloadBookingHistoryPdfQuery,
  useGetCalendarDayConfigQuery,
  useGetCalendarRangeConfigQuery,
  useCreateCalendarDayConfigMutation,
  useCreateOrUpdateCalendarRangeConfigMutation,
} = bookingsApi;
